import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@web/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "quiet-danger";
type Size = "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-accent text-accent-ink hover:brightness-105 active:brightness-95 border-transparent",
  secondary: "bg-paper text-ink border-line-strong hover:bg-sunk",
  ghost: "bg-transparent text-ink-2 border-transparent hover:bg-sunk",
  // Destructive actions stay calm: no red, just a clear label and a confirmation step.
  "quiet-danger": "bg-paper text-ink-2 border-line-strong hover:bg-sunk hover:text-ink",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-sm gap-1.5",
  md: "h-10 px-4 text-[15px] gap-2",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    busy = false,
    className,
    disabled,
    children,
    type,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-lg border font-medium whitespace-nowrap",
        "transition-colors disabled:cursor-not-allowed disabled:opacity-55",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
